import { useCallback } from 'react';
import { useCurrentStateAndParams } from '@uirouter/react';

import { setPortainerAgentTargetHeader } from '@/portainer/services/http-request.helper';
import { getContainerLogs } from '@/react/docker/containers/containers.service';
import { useContainer } from '@/react/docker/containers/queries/useContainer';
import { ContainerDetailsViewModel } from '@/docker/models/containerDetails';

import { PageHeader } from '@@/PageHeader';
import { LogViewer } from '@@/LogViewer';
import { GetLogsParamsInterface } from '@@/LogViewer/types';
import { InformationPanel } from '@@/InformationPanel';
import { TextTip } from '@@/Tip/TextTip';
import { Link } from '@@/Link';

export function LogView() {
  const {
    params: { endpointId: environmentId, id: containerId, nodeName },
  } = useCurrentStateAndParams();

  setPortainerAgentTargetHeader(nodeName);

  const getLogsFn = useCallback(
    ({ since, until, tail, timestamps }: GetLogsParamsInterface) =>
      getContainerLogs(environmentId, containerId, {
        stdout: true,
        stderr: true,
        timestamps,
        tail: tail || 0,
        since,
        until,
      }),
    [environmentId, containerId]
  );

  const containerQuery = useContainer(
    { environmentId, containerId, nodeName },
    {
      select: (c) => new ContainerDetailsViewModel(c),
    }
  );
  if (!containerQuery.data || containerQuery.isLoading) {
    return null;
  }

  const containerName = containerQuery.data.Name?.substring(1) || '';
  const containerStartDate = containerQuery.data.State?.StartedAt;
  const logsEnabled =
    containerQuery.data.HostConfig?.LogConfig?.Type && // if a portion of the object path doesn't exist, logging is likely disabled
    containerQuery.data.HostConfig.LogConfig.Type !== 'none'; // if type === none logging is disabled
  const breadcrumbs = [
    { label: 'Containers', link: 'docker.containers' },
    {
      label: containerName,
      link: 'docker.containers.container',
      linkParams: { id: containerId },
    },
    'Logs',
  ];

  return (
    <>
      <PageHeader title="Container logs" breadcrumbs={breadcrumbs} reload />

      {logsEnabled ? (
        <LogViewer
          resourceType="docker-container"
          resourceName={containerName}
          minDate={
            containerStartDate ? new Date(containerStartDate) : undefined
          }
          getLogsFn={getLogsFn}
        />
      ) : (
        <LogsDisabledInfoPanel />
      )}
    </>
  );
}

function LogsDisabledInfoPanel() {
  const {
    params: { id: containerId, nodeName },
  } = useCurrentStateAndParams();

  return (
    <div className="row">
      <div className="col-sm-12">
        <InformationPanel>
          <TextTip color="blue">
            Logging is disabled for this container. If you want to re-enable
            logging, please{' '}
            <Link
              to="docker.containers.new"
              params={{ from: containerId, nodeName }}
              data-cy="redeploy-container-link"
            >
              redeploy your container
            </Link>{' '}
            and select a logging driver in the &quot;Command & logging&quot;
            panel.
          </TextTip>
        </InformationPanel>
      </div>
    </div>
  );
}
